<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore } from '@/stores/billing';
import { Button, Heading, LayoutGap, Prose } from '@dabble/quillui';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const billingStore = useBillingStore();
const router = useRouter();

const selectedReason = ref<string>('');
const otherReasonDetail = ref<string>('');
const feedback = ref<string>('');

const cancellationReasons = computed(() => [
  { value: 'too_expensive', label: t('exit_survey_reason_too_expensive') },
  { value: 'missing_features', label: t('exit_survey_reason_missing_features') },
  { value: 'found_alternative', label: t('exit_survey_reason_found_alternative') },
  { value: 'temporary_pause', label: t('exit_survey_reason_temporary_pause') },
  { value: 'not_using_enough', label: t('exit_survey_reason_not_using') },
  { value: 'technical_issues', label: t('exit_survey_reason_technical') },
  { value: 'other', label: t('exit_survey_reason_other') },
]);

const isReasonSelected = computed(() => {
  if (!selectedReason.value) return false;
  if (selectedReason.value === 'other') {
    return otherReasonDetail.value.trim().length > 0;
  }
  return true;
});

const handleSubmit = async () => {
  if (!isReasonSelected.value || billingStore.isLoading) return;

  const trimmed = (s: string) => s.trim() || undefined;

  const success = await billingStore.cancelSubscription({
    reason: selectedReason.value,
    feedback: trimmed(feedback.value),
    otherReason: selectedReason.value === 'other' ? trimmed(otherReasonDetail.value) : undefined,
  });

  if (success) {
    setTimeout(() => {
      router.push({ name: 'Billing' });
    }, 500);
  }
};

const goBack = () => {
  router.go(-1);
};
</script>

<template>
  <LayoutGap direction="column" align="center" size="large" class="exit-survey-view">
    <LayoutGap direction="column" size="medium" class="view-container">
      <Heading :level="2" align="center" no-margin class="view-header">{{ t('exit_survey_title') }}</Heading>
      <Prose variant="secondary" align="center" class="view-subheader">{{ t('exit_survey_subheader') }}</Prose>

      <form class="survey-form" @submit.prevent="handleSubmit">
        <LayoutGap direction="column" size="medium" class="form-group">
          <label class="form-label">{{ t('exit_survey_reason_label') }}</label>
          <LayoutGap
            v-for="reason in cancellationReasons"
            :key="reason.value"
            direction="row"
            vertical-align="center"
            size="xsmall"
            class="radio-option"
          >
            <input
              :id="`reason-${reason.value}`"
              v-model="selectedReason"
              type="radio"
              :value="reason.value"
              name="cancellationReason"
              required
            />
            <label :for="`reason-${reason.value}`">{{ reason.label }}</label>
          </LayoutGap>
          <textarea
            v-if="selectedReason === 'other'"
            v-model="otherReasonDetail"
            :placeholder="t('exit_survey_other_placeholder')"
            class="form-textarea other-reason-textarea"
            rows="2"
            required
          ></textarea>
        </LayoutGap>

        <LayoutGap direction="column" size="small" class="form-group">
          <label for="feedback" class="form-label">{{ t('exit_survey_feedback_label') }}</label>
          <textarea
            id="feedback"
            v-model="feedback"
            :placeholder="t('exit_survey_feedback_placeholder')"
            class="form-textarea"
            rows="4"
          ></textarea>
        </LayoutGap>

        <LayoutGap direction="row" align="space-between" vertical-align="center" wrap size="small" class="form-actions">
          <Button
            type="button"
            variant="secondary"
            finish="outlined"
            :disabled="billingStore.isLoading"
            @click="goBack"
          >
            {{ t('exit_survey_go_back') }}
          </Button>
          <Button type="submit" variant="danger" :disabled="billingStore.isLoading || !isReasonSelected">
            <span v-if="billingStore.isLoading">{{ t('exit_survey_processing') }}</span>
            <span v-else>{{ t('exit_survey_cancel_subscription') }}</span>
          </Button>
        </LayoutGap>

        <p v-if="billingStore.error" class="error-message">
          {{ billingStore.error }}
        </p>
      </form>
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.exit-survey-view {
  padding: 2rem;
}

.view-container {
  max-width: 600px;
  width: 100%;
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.view-header {
  color: #dc3545;
}

.view-subheader {
  margin-bottom: 0.5rem;
}

.survey-form {
  width: 100%;
}

.form-label {
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #343a40;
}

.radio-option {
  label {
    cursor: pointer;
    color: #495057;
  }

  input[type='radio'] {
    cursor: pointer;
  }
}

.form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.other-reason-textarea {
  margin-top: 0.5rem;
}

.form-actions {
  margin-top: 1rem;
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
}

.error-message {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 1rem;
  text-align: center;
}
</style>
